import { ReactNode, useState } from "react";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import {
  GridValidRowModel,
  GridRowModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import { ClassNameMap } from "@mui/material";
import { Info } from "luxon";

import {
  ConfigurationTableModel,
  ConfigurationDialogFormType,
  WatchFieldType,
  SelectFilterItemSortedFormDetails,
  EditFormDetails,
  SelectorCalculationType,
  SelectorCalculationMethodType,
  SelectorRoundingDirectionType,
  SelectorLeverTypeType,
  SelectorRadiusOfInterestType,
  SelectorTypeActionType,
  SelectorConstantType,
  SelectorIndustryType,
  SelectorAreaType,
  SelectorBlockNameType,
  SelectorLeverNameType,
  SelectorEventMasterActiveType,
  SelectorEventMasterCategoryType,
  SelectorEventMasterCountryType,
  SelectorEventMasterImpactType,
  SelectorEventMasterScopeType,
  SelectorEventVenueMatchLevelType,
  SelectorEventVenueStatusType,
  SelectorSweepCoordinatesType,
  SelectorSweepFlagType,
  SelectorSweepInterestType,
  SelectorSweepLockType,
  SelectorSweepContractStateType,
  SelectorSweepCompetitorStateType,
  SelectorSweepMatchType,
  SelectorSweepPriorityType,
  SelectorSweepConnectorStatusType,
  SelectorSweepStatusType,
  SelectorTypeImpactType,
  SelectorStatusType,
  FormDetailsType,
  SelectorCommissionMechanismType,
  SelectorLeadTimeOverrideType,
  SelectorRoundingDirectionTypeV2,
  SelectorTypeAncillaryUhcatType,
  SelectorTypeAncillaryGroupType,
  SelectorKpiFormatType,
  StatusTextType,
} from "./interfaces";
import { checkErrors, getDefaultOptions } from "../services/Category";
import { GenericObject } from "../services/Price";
import {
  capitalizeFirstLetter,
  GenericDjangoPaginatedResponse,
  isObject,
} from "../services/utils";
import { Flags, User } from "../AuthProvider";

/**
 * Defines the weekdays as a number
 *
 * Respects the iso representation.
 */
export enum DayOfWeek {
  MONDAY = 1,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
}

export type DayOfWeekTranslationKeyField = { [key in DayOfWeek]: string };

/**
 * Returns a map of weekdays
 *
 * Use luxon to retrieve weekday label with weekday number
 * Info.weekdays() return array, so lenght begin to 0, it's why we set [DayOfWeek[DAY] - 1] because DayOfWeek type begin to 1
 */
export const getDaysOfWeek = (): DayOfWeekTranslationKeyField => ({
  [DayOfWeek.MONDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.MONDAY - 1]!
  ),
  [DayOfWeek.TUESDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.TUESDAY - 1]!
  ),
  [DayOfWeek.WEDNESDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.WEDNESDAY - 1]!
  ),
  [DayOfWeek.THURSDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.THURSDAY - 1]!
  ),
  [DayOfWeek.FRIDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.FRIDAY - 1]!
  ),
  [DayOfWeek.SATURDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.SATURDAY - 1]!
  ),
  [DayOfWeek.SUNDAY]: capitalizeFirstLetter(
    Info.weekdays()[DayOfWeek.SUNDAY - 1]!
  ),
});

/**
 * Remaps the key as a string
 */
export const useGetDaysOfWeekList = () => {
  const days = getDaysOfWeek();

  const [list] = useState<Array<{ name: string; label: string }>>(
    Object.entries(days).map(([name, label]) => ({ name, label }))
  );

  return list;
};

/**
 * Merge a table's configured field options into its form fields.
 *
 * A table whose dialog is a `customForm` configures no options at all, so a
 * missing list means "nothing to merge" rather than a crash: without this the
 * edit action threw "fieldOptions is not iterable" the moment such a row was
 * opened.
 */
export const mergeFormOptions = (editOptions: any, formFields: any) => {
  let mergedFieldsOptions: FormDetailsType = [];
  if (!editOptions) {
    return mergedFieldsOptions;
  }

  const mergeDeepOptions = (
    fieldOptions: any,
    fields: any,
    mergeFieldsOptions: GenericObject[]
  ) => {
    for (const options of fieldOptions) {
      let optionsIndex = fields?.findIndex((el: any) => el.key === options.key);
      if (optionsIndex > -1) {
        // If field has column (field type FieldGroupColumnFormDetails)
        if (options.columns && fields[optionsIndex].columns) {
          const currColumns = { ...fields[optionsIndex] };
          currColumns.columns = [];

          mergeFieldsOptions.push({ ...options, ...currColumns });

          mergeDeepOptions(
            options.columns,
            fields[optionsIndex].columns,
            mergeFieldsOptions[mergeFieldsOptions.length - 1]?.columns ?? []
          );
          // If field has fields (field type FieldGroupFormDetails or FieldGroupColumnFormDetails)
        } else if (options.fields && fields[optionsIndex].fields) {
          const currFields = { ...fields[optionsIndex] };
          currFields.fields = [];

          mergeFieldsOptions.push({ ...options, ...currFields });

          mergeDeepOptions(
            options.fields,
            fields[optionsIndex].fields,
            mergeFieldsOptions[mergeFieldsOptions.length - 1]?.fields ?? []
          );
        } else {
          mergeFieldsOptions.push({ ...options, ...fields[optionsIndex] });
        }
      }
    }
  };

  mergeDeepOptions(editOptions, formFields, mergedFieldsOptions);

  return mergedFieldsOptions;
};

export type ApiError = Error & { details?: unknown };

/**
 * Same contract as `checkErrors`, but keeps the response body on the thrown
 * error. That body is where the API puts its validation messages, and
 * `checkErrors` discards it — leaving the caller with only a status code.
 */
const throwWithApiDetails = async (response: Response) => {
  if (response.ok) return;
  if (response.type === "opaqueredirect") {
    throw new Error("Unauthorized");
  }

  const error: ApiError = new Error(
    `${response.status}: ${response.statusText}`
  );
  try {
    error.details = await response.json();
  } catch {
    error.details = undefined;
  }

  throw error;
};

/**
 * Flatten an API error body into displayable lines. Field errors are prefixed
 * with the field they belong to; `non_field_errors` and `detail` hold messages
 * about the record as a whole, so they are shown as-is.
 */
export const getApiErrorMessages = (details: unknown): string[] => {
  if (typeof details === "string") return [details];
  if (Array.isArray(details)) return details.flatMap(getApiErrorMessages);
  if (details && typeof details === "object") {
    return Object.entries(details).flatMap(([field, value]) => {
      const messages = getApiErrorMessages(value);
      return field === "non_field_errors" || field === "detail"
        ? messages
        : messages.map((message) => `${field}: ${message}`);
    });
  }
  return [];
};

export const basicCreate = async <T>(
  url: string,
  data: T,
  options: GenericObject = {}
): Promise<T> => {
  const _url = new URL(`${url}`, window.origin);
  const _options = { method: "POST", body: JSON.stringify(data), ...options };

  const response = await fetch(_url.toString(), getDefaultOptions(_options));

  await throwWithApiDetails(response);

  return response.json();
};

export const getImportUrl = async (url: string, method: string = "GET") => {
  const _url = new URL(`${url}`, window.origin);
  const _options = { method: method };

  const response = await fetch(_url.toString(), {
    headers: {},
    redirect: "manual",
    ..._options,
  });

  checkErrors(response);

  return response;
};

export const sendFile = async (
  url: string,
  data: any,
  method: string = "PUT"
) => {
  const _url = new URL(`${url}`, window.origin);
  const _options = { method: method, body: data };

  const response = await fetch(_url.toString(), {
    headers: { "x-amz-content-sha256": "UNSIGNED-PAYLOAD" },
    redirect: "manual",
    ..._options,
  });

  checkErrors(response);

  return response;
};

export const basicUpdate = async <
  T
  // TODO: adapt dynamic options type (body)
  // U extends GenericObject | undefined = undefined
>(
  url: string,
  data: T,
  options: GenericObject = {}
): Promise<T> => {
  const _url = new URL(`${url}`, window.origin);
  const _options = { method: "PUT", body: JSON.stringify(data), ...options };

  const response = await fetch(_url.toString(), getDefaultOptions(_options));

  await throwWithApiDetails(response);

  return response.json();
};

export const basicDelete = async <
  T
  // TODO: adapt dynamic options type (body)
  // U extends GenericObject | undefined = undefined
>(
  url: string,
  data: GenericObject = {},
  options: GenericObject = {}
): Promise<T | void> => {
  const _url = new URL(`${url}`, window.origin);
  const _options = { method: "DELETE", body: JSON.stringify(data), ...options };

  const response = await fetch(_url.toString(), getDefaultOptions(_options));

  checkErrors(response);
};

export const bulkUpdate = async <T>(
  url: string,
  data: GenericObject[],
  options: GenericObject = {}
): Promise<T> => {
  const _url = new URL(`${url}`, window.origin);
  const _options = { method: "PATCH", body: JSON.stringify(data), ...options };

  const response = await fetch(_url.toString(), getDefaultOptions(_options));

  await throwWithApiDetails(response);

  return response.json();
};

export const fetchApiObject = async (
  apiUrl: string,
  apiParams?: GenericObject
): Promise<GenericObject> => {
  const params = new URLSearchParams(apiParams as Record<string, string>);
  const url = new URL(apiUrl, window.origin);
  url.search = params.toString();
  const response = await fetch(url.toString(), getDefaultOptions());
  checkErrors(response);
  return response.json();
};

export const getSelectFilterValues = async <T>(
  apiUrl: string,
  apiParams?: GenericObject,
  initialValues?: string | null
): Promise<GenericDjangoPaginatedResponse<T>> => {
  const params = new URLSearchParams(apiParams);
  const url = new URL(apiUrl, window.origin);
  url.search = params.toString();

  if (initialValues) {
    url.searchParams.append("search", initialValues);
  }

  const response = await fetch(url.toString(), getDefaultOptions());
  checkErrors(response);

  return response.json();
};

/**
 * Get the value of a nested object.
 */
export const reducePath = (initialState: GridValidRowModel, value: string[]) =>
  value.reduce((a: GenericObject, b: string) => a && a[b], initialState);

/**
 * Create Title for dialog
 * Change the title depending on what form is open (Create, Change, Mass Change ...)
 */
export const useCreateTitle = (
  isSubFormOpen: boolean,
  dialogFormType: ConfigurationDialogFormType,
  model: ConfigurationTableModel,
  row: GridRowModel
) => {
  const intl = useIntl();

  if (isSubFormOpen) {
    return intl.formatMessage(
      {
        id: "CONFIGURATION_CREATE_MODEL",
      },
      { model: intl.formatMessage({ id: model.label }) }
    );
  } else if (dialogFormType === ConfigurationDialogFormType.EDIT && row) {
    return intl.formatMessage(
      {
        id: "CONFIGURATION_CHANGE_MODEL",
      },
      {
        model: intl.formatMessage({ id: model.label }),
        name: reducePath(row, model.entityName.split(".")) as any,
      }
    );
  } else if (dialogFormType === ConfigurationDialogFormType.MASS_EDIT) {
    return intl.formatMessage(
      {
        id: "CONFIGURATION_MASS_CHANGE_MODEL",
      },
      {
        model: intl.formatMessage({ id: model.label }),
      }
    );
  } else if (dialogFormType === ConfigurationDialogFormType.CREATE) {
    return intl.formatMessage(
      {
        id: "CONFIGURATION_CREATE_MODEL",
      },
      { model: intl.formatMessage({ id: model.label }) }
    );
  } else if (dialogFormType === ConfigurationDialogFormType.FREE) {
    return intl.formatMessage({
      id: model.label,
    });
  } else {
    return "";
  }
};

export const useCreateSubtitle = (
  isSubFormOpen: boolean,
  dialogFormType: ConfigurationDialogFormType,
  model: ConfigurationTableModel,
  row: GridRowModel
): ReactNode | undefined => {
  const intl = useIntl();

  if (
    isSubFormOpen ||
    !model.subtitle ||
    (!model.subtitle.dialogFormTypes.includes(dialogFormType) &&
      model.subtitle.dialogFormTypes !== "all")
  ) {
    return undefined;
  }

  if (typeof model.subtitle.label === "string") {
    return intl.formatMessage({ id: model.subtitle.label });
  }

  return model.subtitle.label(row, intl);
};

export const createUrlParams = (url: string, params?: GenericObject) => {
  if (params) {
    const concatedValues = Object.entries(params).reduce(
      (acc, curr, index, array) => {
        const [key, value] = curr;
        const isLastItem = index === array.length - 1;

        return isLastItem
          ? (acc += `${key}=${value}`)
          : (acc += `${key}=${value}&`);
      },
      `${url}/?`
    );

    return concatedValues;
  }

  return `${url}/`;
};

/**
 * Split string and transform a[0] => a.0
 * Use to get path of object
 */
export const splitPath = (path: string): string[] => {
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);
};

const reducePathCustom = (obj: any, pathArray: string[]): any => {
  return pathArray.reduce((acc, key) => acc?.[key], obj);
};

/**
 * Get value of field watched only if wachfield is an array
 * (values come from Formik form)
 */
export const useGetWatchedFieldValue = (
  values: GenericObject,
  fieldName: string,
  watchFields?: WatchFieldType | string
) => {
  if (!watchFields || typeof watchFields === "string") return undefined;

  const params: GenericObject = {};

  const fieldPathArray = splitPath(fieldName);
  const parentPath = fieldPathArray.slice(0, -1);

  watchFields.forEach((field) => {
    let transformedWatchKey = "";

    // In case of nested form, we possibly want to look after field inside the form or in the parent form
    // We make the differentiation with "[]" inside the watchFieldPath
    if (field.watchFieldPath.includes("[]")) {
      const splitParent = field.watchFieldPath.split("[].");
      transformedWatchKey = splitParent[1]
        ? splitParent[1].replace(/\./g, "__")
        : field.watchFieldPath.replace(/\./g, "__");
    } else {
      transformedWatchKey = field.watchFieldPath.replace(/\./g, "__");
    }

    const watchedFieldPath = field.watchFieldPath.includes("[]")
      ? [...parentPath, transformedWatchKey]
      : [transformedWatchKey];

    const watchedValue = reducePathCustom(values, watchedFieldPath);
    const finalValue = field.paramsValue
      ? reducePathCustom(watchedValue, field.paramsValue.split("."))
      : watchedValue;

    if (finalValue !== undefined) {
      params[field.paramsKey] = finalValue;
    }
  });

  return params;
};

/**
 * Create api params object
 */
export const useCreateParams = (
  filter: SelectFilterItemSortedFormDetails | EditFormDetails,
  dynamicPath?: boolean,
  row?: GridRowModel,
  rows?: GridRowModel[],
  watchedFieldValue?: GenericObject,
  multipleSelection?: GridRowSelectionModel,
  isMappingUhCat?: boolean
): GenericObject => {
  const params: GenericObject = {};

  if (filter.apiParams) {
    for (const [key, value] of Object.entries(filter.apiParams)) {
      //Simple update
      if (isObject(value) && row) {
        const data = (value as any).path.split(".");
        params[key] = reducePath(row, data);

        //Mass Update (for now, need to be specific for mapping UHCAT)
      } else if (
        isObject(value) &&
        rows &&
        rows[0] &&
        multipleSelection &&
        isMappingUhCat
      ) {
        //If same UH CAT property are selected, display only uhcat related to the current property
        const data = (value as any).path.split(".");
        params[key] = reducePath(rows[0], data);
      } else if (!isObject(value)) {
        params[key] = value;
      }
    }
  }

  if (
    dynamicPath &&
    watchedFieldValue !== undefined &&
    watchedFieldValue !== null
  ) {
    return {
      ...params,
      ...watchedFieldValue,
    };
  }

  return params;
};

/**
 * Check if all watched field are filled
 */
export const checkIfAllWatchFieldsValueAreFilled = (
  apiParams: GenericObject,
  watchFields?: WatchFieldType | string
): boolean => {
  if (watchFields && typeof watchFields !== "string") {
    return watchFields.every(
      (field) =>
        field.optional ||
        (apiParams.hasOwnProperty(field.paramsKey) &&
          apiParams[field.paramsKey])
    );
  }

  return true;
};

export const useMaxFromApi = (
  values: GenericObject,
  fieldName: string,
  maxConfig:
    | { api: string; watchField: WatchFieldType; responseKey?: string }
    | undefined
): number | undefined => {
  const watchedFieldValueForMax = useGetWatchedFieldValue(
    values,
    fieldName,
    maxConfig?.watchField
  );
  const allMaxFieldsFilled = maxConfig
    ? checkIfAllWatchFieldsValueAreFilled(
        watchedFieldValueForMax ?? {},
        maxConfig.watchField
      )
    : false;
  const { data: maxApiData } = useQuery(
    [maxConfig?.api, watchedFieldValueForMax],
    () => fetchApiObject(maxConfig!.api, watchedFieldValueForMax ?? {}),
    { enabled: !!maxConfig && allMaxFieldsFilled }
  );
  return maxApiData != null && maxConfig != null
    ? maxApiData[maxConfig.responseKey ?? "available_rooms"]
    : undefined;
};

/**
 * List of static selects
 *
 * key : name of selector, use in conf file to set selector to use
 * value : options of static selector
 */
export const SELECTORS_OPTIONS: any = {
  format_preset: SelectorKpiFormatType,
  action_type: SelectorTypeActionType,
  ancillary_margins_uhcat: SelectorTypeAncillaryUhcatType,
  ancillary_margins_group: SelectorTypeAncillaryGroupType,
  block_name: SelectorBlockNameType,
  calculation_method: SelectorCalculationMethodType,
  calculation_method_1: SelectorCalculationMethodType,
  calculation_method_2: SelectorCalculationMethodType,
  calculation_type: SelectorCalculationType,
  calculation_type_1: SelectorCalculationType,
  calculation_type_2: SelectorCalculationType,
  commission_mechanism: SelectorCommissionMechanismType,
  constants_name: SelectorConstantType,
  impact: SelectorTypeImpactType,
  event_master_impact: SelectorEventMasterImpactType,
  event_master_category: SelectorEventMasterCategoryType,
  event_master_scope: SelectorEventMasterScopeType,
  event_master_country: SelectorEventMasterCountryType,
  event_master_is_active: SelectorEventMasterActiveType,
  event_venue_match_level: SelectorEventVenueMatchLevelType,
  event_venue_status: SelectorEventVenueStatusType,
  sweep_priority: SelectorSweepPriorityType,
  sweep_interest_rating: SelectorSweepInterestType,
  sweep_status: SelectorSweepStatusType,
  sweep_connector_status: SelectorSweepConnectorStatusType,
  sweep_rating_lock: SelectorSweepLockType,
  sweep_match: SelectorSweepMatchType,
  sweep_contract_state: SelectorSweepContractStateType,
  sweep_competitor_state: SelectorSweepCompetitorStateType,
  sweep_coordinates: SelectorSweepCoordinatesType,
  sweep_flag: SelectorSweepFlagType,
  industry: SelectorIndustryType,
  lead_time_override_global: SelectorLeadTimeOverrideType,
  lead_time_override_model: SelectorLeadTimeOverrideType,
  lever_name: SelectorLeverNameType,
  lever_type: SelectorLeverTypeType,
  radius_of_interest: SelectorRadiusOfInterestType,
  rounding_direction: SelectorRoundingDirectionType,
  rounding_direction_1: SelectorRoundingDirectionType,
  rounding_direction_2: SelectorRoundingDirectionType,
  rounding_direction_v2: SelectorRoundingDirectionTypeV2,
  status: SelectorStatusType,
  granularity: SelectorAreaType,
};

/**
 * Remove columns and inputs from data when current industry not match with includedIndustries param
 *
 * @param data
 * @param userData
 */
export const deleteContextFieldsIndustries = <T>(
  data: T[],
  userData: User | null
) => {
  return data.filter(
    (option: any) =>
      !option.includedIndustries ||
      (userData &&
        option.includedIndustries.includes(userData?.tenant.industry))
  );
};

export const getExportImportTask = async (
  exportPath: string,
  options?: GenericObject
) => {
  const url = new URL(exportPath, window.origin);

  const response = await fetch(
    url.toString(),
    getDefaultOptions({
      method: "GET",
      headers: {
        ...options,
      },
    })
  );

  checkErrors(response);

  return response.json();
};

export async function getExportFile(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Échec du téléchargement");
  }

  const blob = await response.blob();
  const filename = url.split("/").pop()?.split("?")[0] ?? "export.xlsx";

  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(downloadUrl);
}

export const isRequireFlagAccess = (
  item: GenericObject,
  user: User | null
): boolean => {
  if (!item.flagRequired) return true;

  const flagsComparator: { key: keyof Flags; value: string } = {
    key:
      typeof item.flagRequired === "string"
        ? item.flagRequired
        : item.flagRequired.flag,
    value:
      typeof item.flagRequired === "string" ? "1" : item.flagRequired.value,
  };

  return user?.tenant?.flags[flagsComparator.key] === flagsComparator?.value;
};

export function getClassSafe(key: string, classes: ClassNameMap) {
  return key in classes ? classes[key as keyof typeof classes] : "";
}

export function getStatusClassName(status: StatusTextType) {
  switch (status) {
    case "info":
      return "infoText";
    case "warning":
      return "warningText";
    case "error":
      return "errorText";
    case "nancie":
      return "nancieText";
  }
}

// Generate className for text Field (ConfigurationFormType.Text)
export function getTextFieldClassName(
  classes: ClassNameMap,
  className?: string[],
  statusClassName?: string
) {
  let baseClassName = [];

  if (className) {
    baseClassName.push(...className.map((key) => getClassSafe(key, classes)));
  }
  if (statusClassName) {
    baseClassName.push(getClassSafe(statusClassName, classes));
  }

  return baseClassName.filter(Boolean).join(" ");
}
